import { useEffect } from 'react'
import { Link, useLocation } from '@tanstack/react-router'
import { IoMdClose } from 'react-icons/io'
import {
  RiFolder2Line,
  RiInboxArchiveLine,
  RiHome5Line,
  RiShieldUserLine,
  RiAdminLine,
  RiMagicLine,
  RiRepeatLine,
  RiTimeLine,
  RiUserStarLine,
  RiFileList3Line,
} from 'react-icons/ri'
import type { IconType } from 'react-icons'

import { useAuth } from '@/core/hooks/use-auth'
import { useTheme } from '@/core/hooks/use-theme'
import { ROUTES } from '@/core/constants/routes'
import type { TPermissionKey } from '@/core/types/permission'

type TSidebarProps = {
  sidebarOpen: boolean
  setSidebarOpen: (sidebarOpen: boolean) => void
}

type TNavItem = {
  to: string
  testId: string
  label: string
  icon: IconType
  exact?: boolean
  show: boolean
}

type TNavSection = {
  key: string
  label?: string
  items: TNavItem[]
}

const Sidebar = ({ sidebarOpen, setSidebarOpen }: TSidebarProps) => {
  const { pathname } = useLocation()
  const { theme } = useTheme()
  const { hasPermission, isAuthenticated, isLoading } = useAuth()
  const isCanli = theme === 'canli'
  const permissionsReady = isAuthenticated && !isLoading

  const can = (permission: TPermissionKey) => hasPermission(permission)

  const sections: TNavSection[] = [
    {
      key: 'overview',
      items: [
        {
          to: ROUTES.app,
          testId: 'link-dashboard',
          label: 'Yönetim',
          icon: RiHome5Line,
          exact: true,
          show: true,
        },
      ],
    },
    {
      key: 'content',
      label: 'İçerik',
      items: [
        {
          to: ROUTES.categories,
          testId: 'link-categories',
          label: 'Kategoriler',
          icon: RiFolder2Line,
          show: can('category.list'),
        },
        {
          to: ROUTES.questionPools,
          testId: 'link-question-pools',
          label: 'Soru Havuzları',
          icon: RiInboxArchiveLine,
          show: can('question_pool.list'),
        },
        {
          to: ROUTES.questionGeneration,
          testId: 'link-question-generation',
          label: 'Soru Üretimi',
          icon: RiMagicLine,
          show: can('generation.list'),
        },
        {
          to: ROUTES.periodicGeneration,
          testId: 'link-periodic-generation',
          label: 'Periyodik Üretim',
          icon: RiRepeatLine,
          show: can('periodic.list'),
        },
        {
          to: ROUTES.periods,
          testId: 'link-periods',
          label: 'Periyotlar',
          icon: RiTimeLine,
          show: can('period.list'),
        },
      ],
    },
    {
      key: 'access',
      label: 'Erişim',
      items: [
        {
          to: ROUTES.users,
          testId: 'link-users',
          label: 'Kullanıcılar',
          icon: RiAdminLine,
          show: can('user.list'),
        },
        {
          to: ROUTES.roles,
          testId: 'link-roles',
          label: 'Roller',
          icon: RiShieldUserLine,
          show: can('role.list'),
        },
        {
          to: ROUTES.auditLogs,
          testId: 'link-audit',
          label: 'Denetim İzi',
          icon: RiFileList3Line,
          show: can('audit.read'),
        },
      ],
    },
    {
      key: 'account',
      items: [
        {
          to: ROUTES.profile,
          testId: 'link-profile',
          label: 'Profil',
          icon: RiUserStarLine,
          show: true,
        },
      ],
    },
  ]

  useEffect(() => {
    setSidebarOpen(false)
  }, [pathname, setSidebarOpen])

  const visibleSections = permissionsReady
    ? sections
        .map((s) => ({ ...s, items: s.items.filter((i) => i.show) }))
        .filter((s) => s.items.length > 0)
    : []

  const matchesPath = (to: string, exact?: boolean) => {
    if (exact) return pathname === to
    // Match exact path or strict subpath to avoid false positives like
    // `/categories` matching against `/categories-foo`.
    return pathname === to || pathname.startsWith(`${to}/`)
  }

  // Only the most specific matching item stays active, so a nested route doesn't
  // also light up its parent.
  const activeTo = visibleSections
    .flatMap((s) => s.items)
    .filter((i) => matchesPath(i.to, i.exact))
    .reduce((best, i) => (i.to.length > best.length ? i.to : best), '')

  const navyBg =
    'bg-[#2a2a5e] bg-[linear-gradient(180deg,#3a306f_0%,#2a2a5e_55%,#1f1f4d_100%)]'

  const navClass = isCanli
    ? `fixed top-0 bottom-0 left-0 z-30 flex h-dvh w-64 flex-col transform border-r border-white/10 ${navyBg} shadow-[8px_0_30px_-12px_rgba(20,16,60,0.6)] transition-transform duration-300 ease-in-out lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`
    : `fixed top-0 bottom-0 left-0 z-30 flex h-dvh w-64 flex-col transform border-r border-white/10 ${navyBg} shadow-[8px_0_30px_-12px_rgba(20,16,60,0.55)] transition-transform duration-300 ease-in-out lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`

  const linkClass = (active: boolean) => {
    const base =
      'group relative flex items-center gap-3 rounded-xl px-3 py-2.5 my-0.5 text-sm transition-all duration-200'
    return active
      ? `${base} bg-white/25 text-white font-semibold shadow-[0_4px_18px_-6px_rgba(124,92,255,0.7)] ring-1 ring-white/30`
      : `${base} text-white/60 font-normal hover:text-white hover:bg-white/10 hover:translate-x-0.5`
  }

  const iconClass = (active: boolean) => {
    const base =
      'shrink-0 text-[20px] leading-none transition-transform duration-200 group-hover:scale-110'
    return active
      ? `${base} text-white`
      : `${base} text-white/60 group-hover:text-white`
  }

  const sectionLabelClass =
    'px-3 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-white/40'

  const sectionDividerClass = 'my-2 border-t border-white/10'

  const closeBtnClass =
    'fixed top-3 right-3 z-21 rounded-md bg-[#2a2a5e] p-2 text-white border border-white/15 lg:hidden'

  return (
    <>
      {sidebarOpen && (
        <>
          <button
            type="button"
            onClick={() => setSidebarOpen(false)}
            data-testid="sidebar-overlay"
            aria-label="Menüyü kapat"
            className="bg-opacity-50 fixed inset-0 z-20 bg-black/25 lg:hidden"
          />
          <button
            type="button"
            onClick={() => setSidebarOpen(false)}
            data-testid="sidebar-close-btn"
            aria-label="Menüyü kapat"
            className={closeBtnClass}
          >
            <IoMdClose size={24} className="shrink-0" />
          </button>
        </>
      )}
      <nav data-testid="sidebar-nav" className={navClass}>
        <Link
          to={ROUTES.app}
          className="mt-6 mb-2 flex h-14 items-center justify-center px-6"
          data-testid="sidebar-logo"
        >
          <img
            src="/logo.png"
            alt="Bilgilig"
            className={
              isCanli
                ? 'h-full w-auto object-contain drop-shadow-[0_0_18px_rgba(124,92,255,0.55)]'
                : 'h-full w-auto object-contain'
            }
          />
        </Link>

        <div className="flex-1 overflow-y-auto px-2 pb-4">
          {!permissionsReady &&
            Array.from({ length: 4 }).map((_, i) => (
              <div
                key={`sk-${i}`}
                className="mx-1 my-2 h-9 animate-pulse rounded-xl bg-white/5"
              />
            ))}
          {visibleSections.map((section, sectionIdx) => (
            <div key={section.key}>
              {section.label && (
                <div className={sectionLabelClass}>{section.label}</div>
              )}
              {!section.label && sectionIdx > 0 && (
                <div className={sectionDividerClass} />
              )}
              {section.items.map(({ to, testId, label, icon: Icon }) => {
                const active = to === activeTo
                return (
                  <Link
                    key={testId}
                    to={to}
                    data-testid={testId}
                    className={linkClass(active)}
                  >
                    <Icon className={iconClass(active)} />
                    <span className="truncate">{label}</span>
                  </Link>
                )
              })}
            </div>
          ))}
        </div>
      </nav>
    </>
  )
}

export default Sidebar
